/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import {
  EnhancementObject,
  ProcessingState,
} from "components/EnhancedSpeech/useEnhanceSpeech";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { addToast } from "actions/ToastMessageActions";

export interface EnhanceSpeechTimeout {
  onLoadingSuccess: VoidFunction;
  onLoadingFailed: (error: MediaError | null) => void;
  hasTimedOut: boolean;
}

enum GlobalTimeoutState {
  loading,
  loaded,
  failed,
  timedOut,
}

export const useGlobalEnhanceSpeechTimeout = (
  enhancement: EnhancementObject | undefined
): EnhanceSpeechTimeout => {
  const [timeoutState, setTimeoutState] = useState(GlobalTimeoutState.loading);
  const [loadingStartTime, setLoadingStartTime] = useState(0);
  const dispatch = useDispatch();

  const trackId = enhancement?.track.id;

  useEffect(() => {
    setTimeoutState(GlobalTimeoutState.loading);
    setLoadingStartTime(new Date().getTime());
  }, [trackId]);

  const isUploading =
    enhancement?.processingState === ProcessingState.uploading;

  const isFinishedWithUpsellError =
    enhancement?.processingState === ProcessingState.finished &&
    enhancement?.track.errorType !== null;

  useEffect(() => {
    if (!trackId || timeoutState === GlobalTimeoutState.loaded || isUploading) {
      return;
    }

    const handleTimeout = (): void => {
      if (
        timeoutState === GlobalTimeoutState.loading &&
        !isFinishedWithUpsellError
      ) {
        setTimeoutState(GlobalTimeoutState.timedOut);

        if (!isFinishedWithUpsellError) {
          dispatch(
            addToast({
              message: I18n.t("enhance_speech.global_timeout"),
            })
          );
        }

        newrelic.noticeError("Free enhance speech timeout.", {
          enhanceSpeechTrackId: trackId,
        });
      }
    };

    // 13 minutes, a little above the 10 minutes we allot for enhancing to allow for download.
    // the timer will reset once the file has been uploaded.
    const timeout = process.env.IS_TEST ? 5 : 60 * 13;
    const timeoutId = setTimeout(handleTimeout, timeout * 1000);

    return (): void => {
      clearTimeout(timeoutId);
    };
  }, [
    dispatch,
    trackId,
    timeoutState,

    // this is important to not be the full enhancement.processingState because
    // we don't want to reset the timer every time that changes.
    isUploading,
    isFinishedWithUpsellError,
  ]);

  const onLoadingSuccess = useCallback(() => {
    setTimeoutState((prev) => {
      if (prev === GlobalTimeoutState.loading) {
        return GlobalTimeoutState.loaded;
      } else {
        return prev;
      }
    });
  }, []);

  const onLoadingFailed = useCallback(
    async (error: MediaError | null) => {
      if (timeoutState === GlobalTimeoutState.failed) {
        return;
      }

      if (!enhancement?.track?.createdAt) {
        return;
      }

      const createdAt = Date.parse(enhancement?.track?.createdAt);
      const threeHoursAgo = new Date().setHours(new Date().getHours() - 3);
      newrelic.noticeError("Audio tag failed to download file.", {
        errorClass: error?.constructor?.name ?? "",
        errorCode: error?.code ?? "",
        errorMessage: error?.message ?? "",
        navigatorOnLine: String(navigator.onLine),
        isSignedInUser: String(window.adobeIMS?.isSignedInUser()),
        unloaded: String(window.unloaded),
        enhancementExpired: String(createdAt < threeHoursAgo),
        loadingTimeInMs: String(new Date().getTime() - loadingStartTime),
      });

      setTimeoutState(GlobalTimeoutState.failed);
      dispatch(addToast({ message: I18n.t("enhance_speech.preload_failed") }));
    },
    [dispatch, enhancement?.track?.createdAt, loadingStartTime, timeoutState]
  );
  return {
    onLoadingSuccess,
    onLoadingFailed,
    hasTimedOut: timeoutState === GlobalTimeoutState.timedOut,
  };
};
